<template>
  <AppHeader>Customer Location</AppHeader>

  <div id="mapbox" style="min-height: 500px; max-width: 100%;"></div>

  <div class="p-4">
    <div class="rounded-2xl inline-block w-full px-3 py-2 bg-white shadow">
      <p class="text-xl text-gray-900">{{ customer.name }} <small>(#{{ customer.id }})</small></p>
      <p class="mt-1 text-xl"><b>{{ toInr(customer.wallet_balance) }}</b></p>
      <p class="mt-2"><b>{{ customer.group.name }}</b></p>
      <p class="mt-1"><b>{{ customer.area.name }}, {{ customer.district.name }}</b></p>
      <p class="mt-1">{{ customer.address }}</p>
    </div>

    <div class="rounded-2xl gap-x-2 flex items-center justify-between w-full p-2 mt-5 bg-white shadow">
      <input v-model="form.latitude" class="border-slate-200 w-full px-2 text-lg border rounded-lg" placeholder="Latitude" type="number" inputmode="decimal">
      <input v-model="form.longitude" class="border-slate-200 w-full px-2 text-lg border rounded-lg" placeholder="Longitude" type="number" inputmode="decimal">
      <button @click="setLocation" class="flex-grow px-3 py-1 text-white bg-green-700 rounded-lg"><i class="ion-md-pin"></i></button>
    </div>

    <div class="mt-5 space-x-2 text-center">
      <button class="px-4 py-2 text-white bg-green-700 rounded-full shadow" @click="getDirection">Open in Map</button>
      <button class="px-4 py-2 text-white bg-green-700 rounded-full shadow" @click="getDirection">Get Direction</button>
    </div>
  </div>
</template>

<script>
import { Geolocation } from '@capacitor/geolocation';
import mixin from '@/utils/mixin.js';
import SetLocation from '@/utils/CustomerMapControl.js';
import AppHeader from '@/components/common/Header.vue';

export default {
  name: 'CustomerMapLocation',
  mixins: [ mixin ],
  components: { AppHeader },
  data() {
    return {
      state: 'idle',
      customer: { area: {}, group: {}, district: {} },
      map: null,
      marker: null,
      data: { latitude: 13.0747, longitude: 80.2165 },
      form: { latitude: null, longitude: null },
    }
  },

  watch: {
    'form.latitude': 'updateMarkerPosition',
    'form.longitude': 'updateMarkerPosition',
  },

  methods: {
    setupMap() {
      mapboxgl.accessToken = this.$store.state.mapboxKey;

      this.map = new mapboxgl.Map({
        container: 'mapbox',
        style: 'mapbox://styles/mapbox/satellite-streets-v11',
        center: [80.2165, 13.0747],
        zoom: 14,
      });

      this.marker = new mapboxgl.Marker({
          color: "#699543",
          draggable: true
        })
        .setLngLat([80.2165, 13.0747])
        .addTo(this.map);

      let geolocate = new mapboxgl.GeolocateControl({ positionOptions: { enableHighAccuracy: true } });

      geolocate.on('geolocate', pos => {
        this.marker.setLngLat([pos.coords.longitude, pos.coords.latitude]);
        this.map.setCenter([pos.coords.longitude, pos.coords.latitude]);
        this.form.latitude = pos.coords.latitude;
        this.form.longitude = pos.coords.longitude;
      });

      this.map.addControl(new mapboxgl.NavigationControl());
      this.map.addControl(geolocate);
      this.map.addControl(new SetLocation(this.setLocation), 'bottom-right');
      this.map.on('click', e => {
        this.marker.setLngLat([e.lngLat.lng, e.lngLat.lat]);
        this.form.latitude = e.lngLat.lat;
        this.form.longitude = e.lngLat.lng;
      });

      this.marker.on('dragend', () => {
        const lngLat = this.marker.getLngLat();
        this.form.latitude = lngLat.lat;
        this.form.longitude = lngLat.lng;
      });

      this.checkPermission();
      this.getData();
    },

    getData() {
      this.state = 'loading';

      this.request('GET', 'customers/'+ this.$route.params.id + '/location', {}, (resp) => {
        this.isLoading = 'idle';
        if (resp.success) {
          this.customer = resp.data;

          if (this.customer.latitude != null && this.customer.longitude != null && this.customer.latitude != '0.00000000' && this.customer.longitude != '0.00000000') {
            this.marker.setLngLat([this.customer.longitude, this.customer.latitude]);
            this.map.setCenter([this.customer.longitude, this.customer.latitude]);
            this.form.latitude = this.customer.latitude;
            this.form.longitude = this.customer.longitude;
          }
        }
      });
    },

    updateMarkerPosition() {
      const lat = Number(this.form.latitude);
      const lng = Number(this.form.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      this.marker.setLngLat([lng, lat]);
      this.map.setCenter([lng, lat]);
    },

    setLocation() {
      if (!this.marker) {
        alert('Please click on the Map to set a Location');
        return;
      }
      let pos = this.marker.getLngLat();

      this.request('POST', 'customers/'+ this.$route.params.id +'/location', {latitude: pos.lat, longitude: pos.lng}, (resp) => {
        alert(resp.message);
      });
    },

    getDirection() {
      if (this.customer.latitude == null && this.customer.longitude == null) alert('Customer Location not Updated');
      else window.open('https://www.google.com/maps/dir/Current+Location/' + this.customer.latitude + ',' + this.customer.longitude, '_system');
    },

    checkPermission() {
      Geolocation
        .checkPermissions()
        .then(e => {
          if (e.location != 'granted' && this.$store.state.platform != 'web') {
            Geolocation
              .requestPermissions('location')
              .catch(e => alert('Please check if GPS is turned On!'));
          }
        })
        .catch(e => alert('Please check if GPS is turned On!'));
    },

    getCurrentLocation() {
      Geolocation
        .getCurrentPosition({enableHighAccuracy: true})
        .then(pos => {
          this.map.setCenter([pos.coords.longitude, pos.coords.latitude]);
          this.marker.setLngLat([pos.coords.longitude, pos.coords.latitude]);
          this.form.latitude = pos.coords.latitude;
          this.form.longitude = pos.coords.longitude;
        })
        .catch(e => alert('Please check if GPS is turned On!'));
    },
  },

  mounted() {
    if (!this.$store.state.api_token || !this.$route.params.id) this.$router.go(-1);
    else this.setupMap();
  },

  unmounted() {
    this.map = null;
    this.marker = null;
  },
}
</script>
